import { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../utils/firebaseConfig';
import { collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import './Jornadas.css';

const defaultFields = [
  { id: '1', name: 'nombre', label: 'Nombre Completo', type: 'text', required: true, order: 0, options: [] },
  { id: '2', name: 'email', label: 'Correo Electrónico', type: 'email', required: true, order: 1, options: [] },
  { id: '3', name: 'telefono', label: 'Teléfono', type: 'tel', required: true, order: 2, options: [] },
  { id: '4', name: 'experiencia', label: 'Experiencia en Futsal', type: 'select', required: false, order: 3, options: ['Principiante', 'Intermedio', 'Avanzado'] },
  { id: '5', name: 'personas', label: 'Número de Personas', type: 'number', required: false, order: 4, options: [] },
  { id: '6', name: 'comentarios', label: 'Comentarios', type: 'textarea', required: false, order: 5, options: [] }
];

export default function FormulariosJornadas() {
  const containerRef = useRef(null);
  const [fields, setFields] = useState(defaultFields);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadFormConfig();

    // Detectar URL ?admin=true
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('admin') === 'true') {
      setIsAdmin(false);
      setAdminPin('');
    }
  }, []);

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminPin === 'MF1234') {
      setIsAdmin(true);
      setAdminPin('');
    } else {
      alert('PIN Incorrecto');
      setAdminPin('');
    }
  };

  // AUTO-RESIZE MEJORADO PARA IFRAME
  useEffect(() => {
    const sendHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.getBoundingClientRect().height;
        window.parent.postMessage({ type: 'FORMULARIO_RESIZE', height: height + 60 }, '*');
      }
    };

    const t1 = setTimeout(sendHeight, 500);
    const t2 = setTimeout(sendHeight, 1500);
    const t3 = setTimeout(sendHeight, 3000);

    window.addEventListener('resize', sendHeight);

    return () => {
      window.removeEventListener('resize', sendHeight);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [fields, formData, showModal]);

  const loadFormConfig = async () => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 5000)
      );

      const loadPromise = getDocs(collection(db, 'formConfig'));
      const querySnapshot = await Promise.race([loadPromise, timeoutPromise]);

      if (!querySnapshot.empty) {
        const config = querySnapshot.docs[0].data();
        const sortedFields = [...(config.fields || [])]
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setFields(sortedFields);

        const initialData = {};
        sortedFields.forEach(field => {
          initialData[field.name] = '';
        });
        setFormData(initialData);
      } else {
        setFields(defaultFields);
        const initialData = {};
        defaultFields.forEach(field => {
          initialData[field.name] = '';
        });
        setFormData(initialData);
      }
    } catch (err) {
      console.error('Error loading form config:', err);
      setFields(defaultFields);
      const initialData = {};
      defaultFields.forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    (fields || []).forEach(field => {
      const value = formData[field.name];

      if (field.required && !value) {
        errors[field.name] = `${field.label} es requerido`;
        isValid = false;
      }

      if (value && field.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.name] = 'Ingresa un correo válido';
          isValid = false;
        }
      }

      if (value && field.type === 'tel') {
        const telRegex = /^[0-9+\s\-()]+$/;
        if (!telRegex.test(value)) {
          errors[field.name] = 'Ingresa un teléfono válido';
          isValid = false;
        }
      }

      if (value && field.type === 'number') {
        if (isNaN(value)) {
          errors[field.name] = 'Ingresa un número válido';
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Por favor completa los campos requeridos correctamente');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      await addDoc(collection(db, 'inscriptions'), {
        data: formData,
        timestamp: serverTimestamp()
      });

      setShowModal(true);

      const initialData = {};
      (fields || []).forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Error al enviar la inscripción. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const hasError = validationErrors[field.name];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
        return (
          <div key={field.id} className={`form-group ${hasError ? 'error' : ''}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type={field.type}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className={`form-input ${hasError ? 'error' : ''}`}
              required={field.required}
            />
            {hasError && <span className="error-message">{validationErrors[field.name]}</span>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className={`form-group ${hasError ? 'error' : ''}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <textarea
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className={`form-input ${hasError ? 'error' : ''}`}
              required={field.required}
              rows="4"
            />
            {hasError && <span className="error-message">{validationErrors[field.name]}</span>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className={`form-group ${hasError ? 'error' : ''}`}>
            <label className="form-label">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <select
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className={`form-input ${hasError ? 'error' : ''}`}
              required={field.required}
            >
              <option value="">Selecciona una opción</option>
              {field.options && field.options.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
            {hasError && <span className="error-message">{validationErrors[field.name]}</span>}
          </div>
        );

      default:
        return null;
    }
  };

  // Mostrar pantalla de PIN si está accediendo como admin pero no está autenticado
  const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isAdminMode = urlParams.get('admin') === 'true';

  if (isAdminMode && !isAdmin) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', fontFamily: 'sans-serif' }}>
        <div style={{ backgroundColor: '#fff', border: '4px solid #000', padding: '48px', maxWidth: '400px', textAlign: 'center', boxShadow: '0 20px 25px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '32px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>ADMIN_GATE</h2>
          <form onSubmit={handleAdminLogin} style={{ display: 'flex', gap: '16px' }}>
            <input
              type="password"
              value={adminPin}
              onChange={e => setAdminPin(e.target.value)}
              style={{ flex: 1, padding: '16px', border: '4px solid #000', fontSize: '24px', fontWeight: 'bold', letterSpacing: '0.2em', textAlign: 'center', outline: 'none' }}
              placeholder="PIN"
              autoFocus
            />
            <button type="submit" style={{ backgroundColor: '#000', color: '#fff', padding: '16px 24px', fontWeight: 'bold', fontSize: '18px', border: 'none', cursor: 'pointer', textTransform: 'uppercase' }}>
              ➜
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', fontFamily: 'sans-serif', color: '#000', paddingBottom: '80px', overflow: 'visible', backgroundColor: '#fff', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@600;700&family=Teko:wght@500;700&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; }
        .f-oswald { font-family: 'Oswald', sans-serif !important; }
        .f-teko { font-family: 'Teko', sans-serif !important; }
        .text-black-imp { color: #000000 !important; }
        .text-red-imp { color: #e30613 !important; }
        @keyframes scan { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }

        @media (max-width: 640px) {
          .hero-padding { padding-left: 16px !important; padding-right: 16px !important; padding-top: 32px !important; padding-bottom: 48px !important; }
          .hero-title { font-size: clamp(1.8rem, 7vw, 3rem) !important; }
          .hero-subtitle { font-size: 12px !important; }
          .escudo-img { height: 100px !important; }
        }
      `}</style>

      {/* HERO SECTION */}
      <div style={{ width: '100%', backgroundColor: '#fff', paddingTop: '64px', paddingBottom: '96px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', paddingLeft: 'clamp(12px, 5vw, 32px)', paddingRight: 'clamp(12px, 5vw, 32px)', textAlign: 'center' }}>
          <div style={{ marginBottom: '48px' }}>
            <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: 900, color: '#e30613', letterSpacing: '0.3em', textTransform: 'uppercase' }}>CANTERA_WANAPIX</span>
          </div>

          <h1 style={{ fontFamily: "'Oswald', sans-serif", fontStyle: 'italic', fontWeight: 900, textTransform: 'uppercase', marginBottom: '24px', lineHeight: '0.8', fontSize: 'clamp(2.5rem, 9vw, 5.5rem)' }}>
            <div style={{ color: '#000000' }}>JORNADAS</div>
            <div style={{ color: '#e30613' }}>PUERTAS ABIERTAS</div>
          </h1>

          {/* ESCUDO */}
          <div style={{ marginBottom: '40px' }}>
            <img
              src="/images/escudo.png"
              alt="Sala10 Zaragoza"
              style={{ height: '150px', width: 'auto', maxWidth: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
            <div style={{ width: '224px', height: '6px', backgroundColor: '#e5e7eb', position: 'relative', borderRadius: '9999px', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: '0', backgroundColor: '#e30613', width: '25%', animation: 'scan 3s infinite linear' }}></div>
            </div>
          </div>

          <p style={{ fontSize: '14px', color: '#6b7280', fontFamily: "'Oswald', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em', maxWidth: '672px', margin: '0 auto', fontWeight: 600, lineHeight: '1.625' }}>
            Únete a nuestras jornadas de puertas abiertas y descubre el fútbol sala
          </p>
        </div>
      </div>

      {/* FORM SECTION */}
      <div style={{ flex: 1, width: '100%', backgroundColor: '#fff' }}>
        <div style={{ maxWidth: '672px', margin: '0 auto', paddingLeft: '16px', paddingRight: '16px', paddingTop: '48px', paddingBottom: '48px' }}>

          {/* MENSAJES */}
          {error && (
            <div style={{ marginBottom: '32px', backgroundColor: '#fee2e2', borderLeft: '4px solid #dc2626', padding: '16px', color: '#991b1b', fontFamily: "'Oswald', sans-serif", textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold', borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ marginBottom: '32px', backgroundColor: '#f0fdf4', borderLeft: '4px solid #16a34a', padding: '16px', color: '#166534', fontFamily: "'Oswald', sans-serif", textTransform: 'uppercase', fontSize: '14px', fontWeight: 'bold', borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}>
              {success}
            </div>
          )}

          {/* FORMULARIO */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {(fields || []).map(field => renderField(field))}

            <div style={{ display: 'flex', gap: '16px', paddingTop: '32px', flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={submitting}
                style={{ flex: 1, minWidth: '150px', backgroundColor: '#000', color: '#fff', paddingTop: '16px', paddingBottom: '16px', fontFamily: "'Oswald', sans-serif", textTransform: 'uppercase', letterSpacing: '0.1em', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '16px', opacity: submitting ? 0.5 : 1, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', border: 'none', transition: 'all 0.3s', borderRadius: '0' }}
              >
                {submitting ? 'INSCRIBIENDO...' : 'INSCRIBIRSE'}
              </button>
              <button
                type="button"
                onClick={() => {
                  const initialData = {};
                  (fields || []).forEach(field => {
                    initialData[field.name] = '';
                  });
                  setFormData(initialData);
                  setValidationErrors({});
                }}
                style={{ flex: 1, minWidth: '150px', backgroundColor: '#f3f4f6', color: '#000', paddingTop: '16px', paddingBottom: '16px', fontFamily: "'Oswald', sans-serif", textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', border: '2px solid #d1d5db', transition: 'all 0.3s', borderRadius: '0' }}
              >
                LIMPIAR
              </button>
            </div>
          </form>
        </div>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: '0', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '48px', maxWidth: '500px', width: '90%', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '28px', fontWeight: 'bold', color: '#000', marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: '1.4' }}>
              GRACIAS POR TU INSCRIPCIÓN
            </h2>
            <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '18px', color: '#333', marginBottom: '32px', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: '1.6', fontWeight: 600 }}>
              NOS PONDREMOS EN CONTACTO CONTIGO A LA MAYOR BREVEDAD POSIBLE
            </p>
            <button
              onClick={() => setShowModal(false)}
              style={{ backgroundColor: '#e30613', color: '#fff', padding: '16px 48px', border: 'none', borderRadius: '4px', fontFamily: "'Oswald', sans-serif", fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.1em', transition: 'all 0.3s', boxShadow: '0 10px 15px -3px rgba(227, 6, 19, 0.3)' }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#c40511'; e.target.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#e30613'; e.target.style.transform = 'translateY(0)'; }}
            >
              CERRAR
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
